'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { AccordionItem } from '@/ui/accordion/AccordionItem'
import type { ProductListFilterFragment } from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useMemo, type RefAttributes } from 'react'
import { productListFiltersMobileItemOpen } from '../../services/productList.store'
import { ProductListFiltersCategoryAccordion } from '../product-list-filters-category/ProductListFiltersCategoryAccordion.client'
import { CATEGORIES_FILTER_NAME } from './ProductListFiltersMobileDrawer.client'

type ProductListFiltersMobileDrawerProps = {
  categoryFilters: ProductListFilterFragment[]
} & RefAttributes<HTMLDivElement>

export function ProductListFiltersMobileCategories({
  ref,
  categoryFilters,
}: ProductListFiltersMobileDrawerProps) {
  const { t } = useTranslation(['productListFilters'])
  const filterNameActive = useAtomValue(productListFiltersMobileItemOpen)

  const totalCategoryItems = useMemo(
    () => categoryFilters.reduce((count, filter) => count + (filter.values?.length ?? 0), 0),
    [categoryFilters],
  )

  const numberOfActiveCategoryFilters = useMemo(() => {
    if (!categoryFilters) return 0

    return categoryFilters.reduce((total, filter) => {
      if (!filter.values) return total

      filter.values.forEach((value) => {
        if (value.__typename === 'ValueLink' && value.values) {
          // eslint-disable-next-line sonarjs/no-nested-functions
          value.values.forEach((subValue) => {
            if (subValue.__typename === 'ValueFilter' && subValue.isActive) {
              total++
            }
          })
        }
      })

      return total
    }, 0)
  }, [categoryFilters])

  return (
    <AccordionItem
      id={CATEGORIES_FILTER_NAME}
      ref={ref}
      defaultExpanded={filterNameActive === CATEGORIES_FILTER_NAME}
      className="w-full"
    >
      <AccordionItem.Header
        title={t('productListFilters:categories', { count: totalCategoryItems })}
        notificationCount={
          numberOfActiveCategoryFilters > 0 ? numberOfActiveCategoryFilters : undefined
        }
      />
      <AccordionItem.Panel>
        {categoryFilters &&
          categoryFilters.map((categoryFilter) => (
            <ProductListFiltersCategoryAccordion
              key={categoryFilter.title}
              categoryFilters={categoryFilter}
              isMobile
            />
          ))}
      </AccordionItem.Panel>
    </AccordionItem>
  )
}
